import React, { useEffect, useMemo } from 'react'
import LeHotelCartReviewTile from 'components/Pages/CartReviewPage/LeHotelCartReviewTile'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Pane from 'components/Common/Pane'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Sheet from 'components/utils/Sheet'
import Divider from 'LuxKit/Divider'
import CartReviewTileOverlay from './_CartReviewTileOverlay'
import zIndex from 'styles/tools/z-index'
import { rem } from 'polished'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import noop from 'lib/function/noop'
import BedbankCartReviewTile from './BedbankCartReviewTile'
import { dateIsBeforeOrSame, formatDynamicDateRange } from 'lib/datetime/dateUtils'
import {
  isBedbankItem,
  isInstantBookingLEHotelItem,
} from 'lib/checkout/checkoutUtils'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LoadingMultiRoomGroupHeaderSkeleton from './LoadingMultiRoomGroupHeaderSkeleton'
import LoadingMultiRoomGroupButtonsSkeleton from './LoadingMultiRoomGroupButtonsSkeleton'
import DeletedCartReviewTile from './DeletedCartReviewTile'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { setCartGroupItemsSaveForLater } from 'actions/AuthActions'
import IconButton from 'LuxKit/Button/IconButton'
import cn from 'clsx'
import offerPageURL from 'lib/offer/offerPageURL'
import { queryKeyAdults, queryKeyCheckIn, queryKeyCheckOut, queryKeyChildren } from 'constants/url'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { getUserCartAccommodationItems } from 'selectors/userCart'
import useOffer from 'hooks/Offers/useOffer'
import NestableClickable from 'components/Common/NestableClickable'

const MultiRoomHotelCartReviewGroupContainer = styled(Pane)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: none;
  ${mediaQueryUp.tablet} {
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
  &.is-condensed {
    border: none;
  }
`
const StyledLeHotelCartReviewTile = styled(LeHotelCartReviewTile)`
  border: none;
`

const StyledBedbankCartReviewTile = styled(BedbankCartReviewTile)`
  border: none;
`

const MultiRoomHotelCartReviewGroupContent = styled(Group)`
  position: relative;
`

const StyledButtonsContainer = styled.div`
  z-index: ${zIndex.reviewCartButtons};
  display: flex;
  justify-content: start;
  column-gap: ${rem(8)};
  position: relative;
`

const CondensedDeleteButton = styled(IconButton)`
  border-color: transparent;
`

interface Props {
  items: Array<App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem>
  banner: React.ReactNode
  showOverlay?: boolean
  hotelName: string
  headerPrice?: React.ReactNode
  checkIn: string
  checkOut: string
  duration: number
  groupDisabled?: boolean
  areAllItemsSavedForLater?: boolean
  areAllItemsDeleted?: boolean
  isAnyItemDeleted?: boolean
  fetching?: boolean
  tileVariant?: 'default' | 'condensed'
  onSaveAllForLater?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onDeleteAll?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

function MultiRoomHotelCartReviewGroup({
  items,
  banner,
  showOverlay,
  hotelName,
  checkIn,
  checkOut,
  duration,
  headerPrice,
  groupDisabled = false,
  areAllItemsSavedForLater = false,
  areAllItemsDeleted = false,
  isAnyItemDeleted = false,
  fetching,
  onSaveAllForLater = noop,
  onDeleteAll = noop,
  tileVariant = 'default',
}: Props) {
  const dispatch = useAppDispatch()
  const hotelItems = useAppSelector(getUserCartAccommodationItems)

  const firstItem = items[0]
  const [offer] = useOffer<App.HotelOffer | App.BedbankOffer>(firstItem.offerId)
  const isPast = dateIsBeforeOrSame(new Date(firstItem.checkIn), new Date())

  const summary = useMemo(() => {
    const dateRange = formatDynamicDateRange(checkIn, checkOut)
    return dateRange ? `${dateRange} · ${items.length} rooms · ${duration} nights` : undefined
  }, [items, checkIn, checkOut, duration])

  const itemIds = useMemo(() => items.map(item => item.itemId), [items])

  const offerLink = useMemo(() => {
    if (!firstItem || !offer) return undefined

    const params = new URLSearchParams()

    const itemGroup = isInstantBookingLEHotelItem(firstItem) ?
        hotelItems.filter(isInstantBookingLEHotelItem).filter(i => i.offerId === firstItem.offerId && i.checkIn === firstItem.checkIn && i.checkOut === firstItem.checkOut) :
        hotelItems.filter(isBedbankItem).filter(i => i.offerId === firstItem.offerId && i.checkIn === firstItem.checkIn && i.checkOut === firstItem.checkOut)

    const rooms = itemGroup.map(i => i.occupancy)

    if (!isPast) {
      params.set(queryKeyCheckIn, firstItem.checkIn)
      params.set(queryKeyCheckOut, firstItem.checkOut)
    }

    params.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    params.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))

    return offerPageURL(offer, params)
  }, [firstItem, hotelItems, isPast, offer])

  useEffect(() => {
    if (!areAllItemsSavedForLater && groupDisabled && !areAllItemsDeleted && !fetching) {
      // if the group is disabled, we want to make sure that all the items are saved for later
      dispatch(setCartGroupItemsSaveForLater(itemIds, true))
    }
  }, [areAllItemsDeleted, areAllItemsSavedForLater, groupDisabled, itemIds, dispatch, fetching])

  if (areAllItemsDeleted) {
    return <DeletedCartReviewTile title={hotelName} itemIds={itemIds} />
  }

  const disableButtons = areAllItemsDeleted || isAnyItemDeleted || fetching
  const isCondensed = tileVariant === 'condensed'

  const buttons = <>
    {fetching && <LoadingMultiRoomGroupButtonsSkeleton />}
    {!fetching && (
      <StyledButtonsContainer>
        {!groupDisabled && !areAllItemsSavedForLater && (
          <TextButton kind="secondary" size="small" disabled={disableButtons} onClick={onSaveAllForLater}>Save all for later</TextButton>
        )}
        {!groupDisabled && areAllItemsSavedForLater && (
          <TextButton kind="primary" size="small" disabled={disableButtons} startIcon={<LinePlusIcon />} onClick={onSaveAllForLater}>Add</TextButton>
        )}
        <TextButton kind="secondary" size="small" disabled={disableButtons} startIcon={<LineTrashAltIcon />} onClick={onDeleteAll}>Delete all</TextButton>
      </StyledButtonsContainer>
    )}
  </>

  return (
    <MultiRoomHotelCartReviewGroupContainer className={cn({ 'is-condensed': isCondensed })}>
      {banner}
      <MultiRoomHotelCartReviewGroupContent direction="vertical">
        <NestableClickable to={offerLink} target="_blank">
          <Sheet background="neutral-eight" border="transparent" borderRadius="S" padding={isCondensed ? 16 : 20}>
            <Group direction="horizontal" horizontalAlign="space-between">
              {fetching && <LoadingMultiRoomGroupHeaderSkeleton />}
              {!fetching && (
                <Group direction="vertical" gap={2}>
                  <Heading variant="heading6">{hotelName}</Heading>
                  {summary && <BodyText variant="medium">{summary}</BodyText>}
                  {!groupDisabled && headerPrice}
                </Group>
              )}
              {!isCondensed && (
                <CSSBreakpoint min="tablet">
                  {buttons}
                </CSSBreakpoint>
              )}
              {isCondensed && (
                <CondensedDeleteButton kind="secondary" disabled={disableButtons} onClick={onDeleteAll}>
                  <LineTrashAltIcon />
                </CondensedDeleteButton>
              )}
            </Group>
          </Sheet>
        </NestableClickable>
        {items.map((item) => {
          return (
            <React.Fragment key={item.itemId}>
              <Divider kind="primary" direction="horizontal" />
              {isInstantBookingLEHotelItem(item) && <StyledLeHotelCartReviewTile
                key={item.itemId}
                item={item}
                hideTitleAndDates
                hideAvailabilityDetails={groupDisabled}
                disableOverlay={showOverlay} // avoid double overlay (group + individual tile)
                saveForLaterIfUnavailable={false}
                tileVariant={tileVariant}
              />}
              {isBedbankItem(item) && <StyledBedbankCartReviewTile
                key={item.itemId}
                item={item}
                hideTitleAndDates
                hideAvailabilityDetails={groupDisabled}
                disableOverlay={showOverlay} // avoid double overlay (group + individual tile)
                saveForLaterIfUnavailable={false}
                tileVariant={tileVariant}
              />}
            </React.Fragment>
          )
        })}
        <CSSBreakpoint max="mobile">
          <Divider kind="primary" direction="horizontal" />
          <Sheet background="neutral-eight" border="transparent" borderRadius="S" padding="16 16">
            {buttons}
          </Sheet>
        </CSSBreakpoint>
        {showOverlay && <CartReviewTileOverlay />}
      </MultiRoomHotelCartReviewGroupContent>
    </MultiRoomHotelCartReviewGroupContainer>
  )
}

export default MultiRoomHotelCartReviewGroup
